import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWallets } from 'modules/Finance/Wallets/useWallets'
import Price from '../Price'
import styles from 'modules/Finance/Wallets/subcomponents/Chart/Chart.module.scss'
import { ChartSkeleton } from 'modules/Finance/Wallets/subcomponents/Chart/ChartSkeleton'
import { DoughnutChart } from 'modules/Finance/Wallets/subcomponents/Chart/DoughnutChart'
import { Legend } from 'modules/Finance/Wallets/subcomponents/Chart/Legend'

const COLORS = {
  0: '#2C8DFF',
  1: '#3ED0FF',
  2: '#FFD600',
  3: '#82FBE5',
  4: '#AAA2FF',
}

const CATEGORY_TYPES = ['out', 'in']

export const Chart = ({ className = '' }) => {
  const { t } = useTranslation(['wallets'])

  const [categoryActiveType, setCategoryType] = useState('out')

  const {
    transactions: { loading, loaded, categories },
    activeWalletCurrency,
  } = useWallets()

  const stateCategories = useMemo(
    () =>
      Object.entries(categories)
        .filter(([, value = {}]) => 'in' in value || 'out' in value)
        .filter(([, value = {}]) => [categoryActiveType] in value)
        .map(([key, value]) => [key, value[categoryActiveType]]),
    [categories, categoryActiveType],
  )

  const handleClickTabs = (type) => {
    setCategoryType(type)
  }

  if (loading && !loaded) {
    return <ChartSkeleton className={className} />
  }

  if (stateCategories.length === 0) {
    return null
  }

  return (
    <div className={clsx(styles['chart'], className)}>
      <div className={styles['chart__doughnut']}>
        <>
          <DoughnutChart
            currency={activeWalletCurrency}
            labels={stateCategories.map((item) =>
              t(`chart.categories.${item[0]}`),
            )}
            colors={Object.values(COLORS)}
            data={stateCategories.map((item) => item[1])}
          />

          <div className={styles['chart__doughnut-context']}>
            <div className={styles['chart__doughnut-context-price']}>
              <Price
                number={parseFloat(
                  stateCategories.reduce((acc, item) => acc + item[1], 0),
                ).toFixed(2)}
                currency={activeWalletCurrency}
              />
            </div>
            <div className={styles['chart__doughnut-context-tabs']}>
              {CATEGORY_TYPES.map((type, index) => (
                <React.Fragment key={type}>
                  {index > 0 && (
                    <span
                      className={styles['chart__doughnut-context-tabs-dot']}
                    />
                  )}
                  <a
                    className={clsx(
                      styles['chart__doughnut-context-tabs-link'],
                      {
                        [styles['chart__doughnut-context-tabs-link_active']]:
                          categoryActiveType === type,
                      },
                    )}
                    onClick={() => handleClickTabs(type)}
                  >
                    {t('chart.' + type)}
                  </a>
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      </div>
      <Legend
        data={{
          categories: stateCategories,
          colors: COLORS,
          activeWalletCurrency,
        }}
        categoryActiveType={categoryActiveType}
      />
    </div>
  )
}

Chart.propTypes = {
  className: PropTypes.string,
}
